import React, { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { colors } from "../constants/colors";
import { useAudios } from "../hooks/useAudios";
import { useImages } from "../hooks/useImages";
import AudioPlay from "./AudioPlay";
import produce from "immer";
import { ReactComponent as Check } from "../assets/svg/check.svg";
import { ReactComponent as Wrong } from "../assets/svg/wrong.svg";
import { v4 as uuid } from 'uuid'

const LessonDataUnionWithBox = ({ seed, freeze = false, onChange = () => {} }) => {
  const [extractedAudios, setExtractedAudios] = useState([]);
  const [extractedImages, setExtractedImages] = useState([]);

  const images = useImages(extractedImages);
  const audios = useAudios(extractedAudios);

  const [sectorA, setSectorA] = useState([]);
  const [sectorB, setSectorB] = useState([]);

  const [palette, setPalette] = useState([]);
  const [union, setUnion] = useState({ bind: undefined });

  const setSector = {
    SECTOR_A: setSectorA,
    SECTOR_B: setSectorB,
  };

  useEffect(() => {
    setSectorA(initSector(seed.alternatives[0]));
    setSectorB(initSector(seed.alternatives[1]));
    setPalette(
      initColors([
        ...seed.alternatives[0].map((i) => i.color),
        ...seed.alternatives[1].map((i) => i.color),
      ])
    );
    setUnion({ bind: undefined });
    setExtractedAudios([seed.audio]);
    setExtractedImages([seed.image]);
  }, [seed]);

  useEffect(() => {
    onChange({
      ready: isReady(sectorA) && isReady(sectorB),
      data: {
        ...seed,
        alternatives: [sectorA, sectorB],
      },
    });
  }, [seed, sectorA, sectorB, onChange]);

  const isLoaded = ({ image, audio }) =>
    ((image && images[image]) || !image) &&
    ((audio && audios[audio]) || !audio);

  const getUnselectedColor = () => palette.find((i) => !i.selected)?.color;

  const selectColor = (color) => {
    if (!color) return;
    setPalette(
      produce((draftState) => {
        const index = draftState.findIndex((i) => i.color === color);
        if (index >= 0) draftState[index].selected = true;
      })
    );
  };

  const unselectColor = (color) => {
    if (!color) return;
    setPalette(
      produce((draftState) => {
        const index = draftState.findIndex((i) => i.color === color);
        if (index >= 0) draftState[index].selected = false;
      })
    );
  };

  const reset = (sector, index) => {
    const inverse = (sector) =>
      sector === "SECTOR_A" ? "SECTOR_B" : "SECTOR_A"; // FIX WHEN INCREMENTS THE NUMBER OF SECTORS

    setSector[sector](
      produce((draftState) => {
        if (!draftState[index]) return;
        unselectColor(draftState[index].color);

        const pair = draftState[index].pair;

        draftState[index].pair = null;
        draftState[index].color = null;

        if (pair === null) return;

        setSector[inverse(sector)](
          produce((draftState) => {
            draftState[pair].pair = null;
            draftState[pair].color = null;
          })
        );
      })
    );
  };

  const initUnion = (sector1, index1) => {
    reset(sector1, index1);

    const color = getUnselectedColor();

    setSector[sector1](
      produce((draftState) => {
        draftState[index1].color = color;
      })
    );

    return (sector2, index2) => {
      if (sector2 === sector1) {
        reset(sector1, index1);
        return;
      }

      reset(sector2, index2);

      setSector[sector1](
        produce((draftState) => {
          draftState[index1].color = color;
          draftState[index1].pair = index2;
        })
      );

      setSector[sector2](
        produce((draftState) => {
          draftState[index2].color = color;
          draftState[index2].pair = index1;
        })
      );

      selectColor(color);
    };
  };

  const onClick = (sector, index) => {
    if (!union.bind) setUnion({ bind: initUnion(sector, index) });
    else if (union.bind) {
      union.bind(sector, index);
      setUnion({ bind: undefined });
    }
  };

  return (
    <Container freeze={freeze}>
      {isLoaded(seed) ? (
        <>
          <Box>
            {seed.image && <Image src={images[seed.image]} />}
            {(seed.text || seed.audio) && (
              <Row>
                {seed.text && <Text>{seed.text}</Text>}
                {seed.audio && <AudioPlay audio={audios[seed.audio]} />}
              </Row>
            )}
          </Box>
          <Sectors>
            <Sector>
              {sectorA.map((item, index) => (
                <Label
                  key={item.ID_AUTOGENERATED}
                  color={item.color}
                  onClick={() => {
                    onClick("SECTOR_A", index);
                  }}
                  onTouchStart={(e) => {
                    onClick("SECTOR_A", index);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                  }}
                >
                  {item.state && <StateIcon as={stateIcon[item.state]} />}
                  {item.text}
                </Label>
              ))}
            </Sector>
            <Sector>
              {sectorB.map((item, index) => (
                <Label
                  key={item.ID_AUTOGENERATED}
                  color={item.color}
                  onClick={() => {
                    onClick("SECTOR_B", index);
                  }}
                  onTouchStart={(e) => {
                    onClick("SECTOR_B", index);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                  }}
                >
                  {item.state && <StateIcon as={stateIcon[item.state]} />}
                  {item.text}
                </Label>
              ))}
            </Sector>
          </Sectors>
        </>
      ) : (
        <Spinner src="loading-blue.gif" />
      )}
    </Container>
  );
};

const stateIcon = {
  SUCCESS: Check,
  FAILURE: Wrong,
};

const initSector = (seedSector) => {
  let sector = formatSector(seedSector);
  return sector;
};

const formatSector = (sector) =>
  sector.map((item) => ({
    ...item,
    ID_AUTOGENERATED: uuid(),
    color: item.pair === null ? null : item.color,
  }));

const initColors = (arraySelected = []) =>
  produce(colors, (draftState) => {
    draftState.forEach((color, index, array) => {
      array[index] = {
        color: color,
        selected: arraySelected.findIndex((i) => i === color) !== -1,
      };
    });
  });

const isReady = (data) => {
  let ready = true;
  for (let item of data)
    if (item.pair === null) {
      ready = false;
      break;
    }
  return ready;
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.freeze &&
    css`
      pointer-events: none;
    `}
`;

const Sector = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  &:not(:first-child) {
    margin-top: 20px;
  }
`;

const Sectors = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 35px;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 20px;
  overflow: hidden;
  width: 100%;
  margin: 0 auto;
`;

const Image = styled.img`
  width: 100%;
  max-height: 360px;
  object-fit: contain;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px;
  & > * {
    margin: 5px;
  }
`;

const Text = styled.div`
  font-size: 0.9em;
  color: black;
`;

const Label = styled.div`
  position: relative;
  user-select: none;
  font-size: 0.9em;
  background-color: #caedff;
  padding: 10px 15px;
  border-radius: 10px;
  margin: 2px;
  cursor: pointer;
  ${(props) =>
    props.color &&
    css`
      border: 4px solid ${props.color};
      //box-shadow: 0px 0px 0px 4px ${props.color};
    `}
`;

const StateIcon = styled.div`
  width: 25px;
  height: 25px;
  min-width: 25px;
  min-height: 25px;
  position: absolute;
  right: 0px;
  top: 0px;
  transform: translate(35%, -35%);
`;

const Spinner = styled.img`
  margin: auto;
  width: 25px;
  height: 25px;
  display: block;
`;

export default LessonDataUnionWithBox;
